import React, { useState, useContext} from 'react';
import { EnterFirstName, EnterLastName, MiddleName, EmailJobPlaceholder, BirthDateJob, DelimiterPlaceholder } from '../../../../utils/Constant';
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from 'react-feather';
import { addTeacher, getTeachers } from '../../../../Api/apiUtils';
import { toast } from 'react-toastify'; // For showing success/error messages
import { ConfigContext } from '../../../../Api/global/ApiContext';
import ReactSelect from "react-select";
import DatePicker from 'react-datepicker';
import { MixpanelEvents, trackEvent } from '../../../../utils/mixpanelTracking';
import { Page, PageHeader, Card, Button, Field, TextInput, TextArea, Select } from '../../../../ui';
import { dsSelectStyles, DS_SELECT_PREFIX } from '../../Student/AddStudent/dsReactSelect';
import WizardSteps from './WizardSteps';
import './add-teacher.scss';

type Option = { value: string; label: string };

const TRUE_FALSE: Option[] = [
  { value: 'True', label: 'True' },
  { value: 'False', label: 'False' },
];
const YES_NO: Option[] = [
  { value: 'True', label: 'Yes' },
  { value: 'False', label: 'No' },
];

const stepTitles = ["Personal Details", "Academic Details", "Employment & Eligibility", "Other Preferences & Courses"];

const MultiStepForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [birthDate, setBirthDate] = useState<Date | null>(null);
  const [formData, setFormData] = useState<any>({});
  const navigate = useNavigate();
  const config = useContext(ConfigContext);
  const { states = [], genders = [], timezones = [], ageGroups = [], countryCodes = [], courses = [], languages = [] } = config || {};
  const airlineCourseCategories = config?.AirlineCourseCategory || [];
  const courseOptions = courses.map((course:any) => ({
    value: course.v2Id,
    label: course.name,
  }));
  
  const languageOptions = languages.map((language:any) => ({
    value: language.id,
    label: language.name,
  }));
  
  const ageGroupOptions = ageGroups.map((ageGroup:any) => ({
    value: ageGroup.id,
    label: ageGroup.name,
  }));

  const nextStep = () => {
    // Validate current step before proceeding
    if (activeStep === 0) {
      if (!formData.firstName || !formData.lastName || !formData.gender || !formData.email || !formData.mobileNumber || !formData.timeZone) {
        toast.error("Please fill all required fields");
        return;
      }
    } else if (activeStep === 1) {
      if (
        !formData.qualifications?.trim() ||
        formData.experience === undefined ||
        formData.experience === ""
      ) {
        toast.error("Please fill all required fields");
        return;
      }
    } else if (activeStep === 2) {
      if (!formData.employmentType) {
        toast.error("Please fill all required fields");
        return;
      }
    }
    setActiveStep((prev) => Math.min(prev + 1, stepTitles.length - 1));
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (name === "mobileNumber") {
      const maxDigits = String(formData.countryCode || "").trim() === "+91" ? 10 : 15;
      const digitsOnly = value.replace(/\D/g, "").slice(0, maxDigits);
      setFormData((prev: any) => ({ ...prev, mobileNumber: digitsOnly }));
      return;
    }

    // Handle checkbox fields
    if (type === 'checkbox') {
      if (name === 'skipHours') {
        setFormData((prev: any) => ({ ...prev, [name]: checked ? 2 : 12 }));
      } else {
        setFormData((prev: any) => ({ ...prev, [name]: checked }));
      }
    } else if (name === "countryCode") {
      const maxDigits = String(value || "").trim() === "+91" ? 10 : 15;
      setFormData((prev: any) => ({
        ...prev,
        countryCode: value,
        mobileNumber: String(prev.mobileNumber || "")
          .replace(/\D/g, "")
          .slice(0, maxDigits),
      }));
      return;
    } 
    // Handle select fields with 'True'/'False' values
    else if (name === 'isAcademicExpert' || 
        name === 'isBatchOnline' || 
        name === 'is1to1Enabled' ||
        name === 'onlyInternational' ||
        name === 'onlyDomestic' ||
        name === 'markedForOffers' ||
        name === 'isKidsFriendly' ||
        name === 'enableForPaid') {
      const boolValue = value === 'True';
      setFormData((prev: any) => ({
        ...prev,
        [name]: boolValue,
        ...(name === 'isAcademicExpert' && !boolValue ? { aeCourseCategoryId: '' } : {}),
      }));
    } 
    // Handle all other fields
    else {
      setFormData((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const handleBirthDateChange = (date: Date | null) => {
    setBirthDate(date);
    setFormData((prev: any) => ({ ...prev, birthDate: date }));
  };

  const handleSelectChange = (selectedOptions: any, fieldName: string) => {
    setFormData((prev: any) => ({ ...prev, [fieldName]: selectedOptions }));
  };

  const handleSubmit = async () => {
    if (formData.isAcademicExpert && !formData.aeCourseCategoryId) {
      toast.error("Please select a category.");
      return;
    }

    function formatDateToDDMMYYYY(date: Date | string | null) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    }
    try {
      // Extract selected values from formData
      const selectedAgeGroups = formData.ageGroup ? formData.ageGroup.map((ag: any) => `${ag.value}`) : [];
      const selectedCourses = formData.courses ? formData.courses.map((course: any) => course.value) : [];
      const selectedLanguages = formData.languages ? formData.languages.map((lang: any) => `${lang.value}`) : [];

      // Prepare the payload
      const payload = {
        titleName: formData.title || "Miss",
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        emailId: formData.email,
        password: "defaultPassword",
        countryCode: formData.countryCode,
        mobileNumber: formData.mobileNumber,
        gender: formData.gender,
        ageGroupId: selectedAgeGroups,
        birthDate: formatDateToDDMMYYYY(formData.birthDate),
        academicQualifications: formData.qualifications,
        state: formData.state,
        timezone: formData.timeZone,
        isOnlyInternational: formData.onlyInternational === "True" || formData.onlyInternational === true,
        isOnlydomestic: formData.onlyDomestic === "True" || formData.onlyDomestic === true,
        Teacherphotolink: formData.photoLink,
        TeacherVideoLink: formData.videoLink,
        isMarkedForOffers: formData.markedForOffers === "True" || formData.markedForOffers === true,
        employmentType: formData.employmentType === "Full-Time" ? "full_time" : "part_time",
        yearsOfExperience: String(formData.experience),
        preferredGender: formData.gender,
        isKidsFriendly: formData.kidsFriendly === "True" || formData.kidsFriendly === true,
        isAcademicExpert: formData.isAcademicExpert === "True" || formData.isAcademicExpert === true,
        enableForPaid: formData.enableForPaid === "True" || formData.enableForPaid === true,
        ...(formData.aeCourseCategoryId
          ? { aeCourseCategoryId: Number(formData.aeCourseCategoryId) }
          : {}),
        isBatchOffline: formData.eligibleOffline === "True" || formData.eligibleOffline === true,
        isBatchOnline: formData.eligibleOnline === "True" || formData.eligibleOnline === true,
        is1to1Enabled: formData.eligibleOneToOne === "True" || formData.eligibleOneToOne === true,
        languageId: selectedLanguages,
        description: formData.description,
        locale: "en_US",
        isLoginEnabled: true,
        lastLoginTime: "",
        passwordChangedOn: "",
        passwordChangeCount: "0",
        isEmailIdVerified: false,
        isMobileNumberVerified: false,
        otpCode: "",
        referralCode: "",
        referredBy: "",
        readynessForTraining: true,
        yearsOfOnlineExperience: String(formData.experience),
        currentStudentsCount: "0",
        birthPlaceName: "",
        currentAddressDetails: "",
        currentAddressLocality: "",
        currentAddressCityName: "",
        currentAddressDistrictName: "",
        currentAddressStateName: "",
        currentAddressCountryName: "India",
        currentAddressPincode: "256378",
        isPermanentAddressSameAsCurrentAddress: false,
        permanentAddressDetails: "",
        permanentAddressLocality: "",
        permanentAddressCityName: "",
        permanentAddressDistrictName: "",
        permanentAddressStateName: "",
        permanentAddressCountryName: "India",
        permanentAddressPincode: "",
      };

      // Call the addTeacher API
      const response = await addTeacher(payload);

      if (response) {
        toast.success("Teacher added successfully!");
        const responseData = response?.data || {};
        const createdTeacher = responseData?.teacherProfileDetails || responseData?.teacher || responseData?.data || responseData;
        const extractId = (obj: any, keys: string[]): any => {
          if (!obj || typeof obj !== "object") return undefined;
          for (const key of keys) {
            if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") return obj[key];
          }
          for (const value of Object.values(obj)) {
            if (value && typeof value === "object") {
              const found = extractId(value, keys);
              if (found !== undefined) return found;
            }
          }
          return undefined;
        };

        let credentialId = extractId(createdTeacher, [
          "TeacherCredentialId",
          "credentialId",
          "teacherCredentialId",
          "credentialID",
          "credential_id",
        ]);
        let teacherId = extractId(createdTeacher, [
          "TeacherId",
          "teacherId",
          "id",
          "teacherID",
          "teacher_id",
        ]);

        if (!credentialId || !teacherId) {
          try {
            const teacherList = await getTeachers(0, 50, {
              emailId: formData.email,
              mobileNumber: formData.mobileNumber,
            });
            const matchedTeacher = teacherList?.[0];
            credentialId = credentialId || matchedTeacher?.credentialId;
            teacherId = teacherId || matchedTeacher?.teacherId;
          } catch (lookupError) {
            console.error("Teacher lookup after create failed:", lookupError);
          }
        }

        if (credentialId && teacherId) {
          navigate(`/pages/teacher-profile/${credentialId}/${teacherId}`);
        } else {
          console.log("Create teacher response for redirect:", responseData);
          navigate("/pages/teacher-profile");
        }
      } else {
        toast.error("Failed to add teacher");
      }
    } catch (error: any) {
      console.error("Error adding teacher:", error);
      const apiError = error?.response?.data;
      const apiMessage =
        typeof apiError === "string"
          ? apiError
          : apiError?.message || apiError?.error || apiError?.status;
      toast.error(apiMessage || "Failed to add teacher");
    }
  };
  

  // The ds Select/TextArea emit select/textarea events; handleInputChange only reads name/value
  // (and type/checked, which are not used for these), exactly as the reactstrap Inputs did.
  const handleControlChange = (event: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>) =>
    handleInputChange(event as unknown as React.ChangeEvent<HTMLInputElement>);

  const titleOptions: Option[] = [
    { value: "", label: "Title" },
    { value: "Mr", label: "Mr" },
    { value: "Mrs", label: "Mrs" },
    { value: "Miss", label: "Miss" },
  ];
  const genderOptions: Option[] = [
    { value: "", label: "Select gender" },
    ...genders.map((gender: any) => ({ value: gender.name.toString(), label: gender.name })),
  ];
  const countryCodeOptions: Option[] = [
    { value: "", label: "Code" },
    ...countryCodes.map((code: any) => ({ value: code.countryCode, label: code.countryCode })),
  ];
  const timezoneOptions: Option[] = [
    { value: "", label: "Select time zone" },
    ...timezones.map((tz: any) => ({ value: tz.type, label: tz.type })),
  ];
  const stateOptions: Option[] = [
    { value: "", label: "Select state" },
    ...states.map((state: string) => ({ value: state, label: state })),
  ];
  const employmentOptions: Option[] = [
    { value: "", label: "Select employment type" },
    { value: "Full-Time", label: "Full-Time" },
    { value: "Part-Time", label: "Part-Time" },
  ];
  const categoryOptions: Option[] = [
    { value: "", label: formData.isAcademicExpert ? "Select category" : "Not required when Demo (AE) is disabled" },
    ...airlineCourseCategories.map((category: { id: number; name: string; categoryId: number }) => ({
      value: String(category.categoryId),
      label: category.name,
    })),
  ];

  const boolField = (name: string, label: string, options: Option[] = TRUE_FALSE) => (
    <Field label={label} required>
      {(ctl) => (
        <Select {...ctl} name={name} options={options} value={formData[name] ? 'True' : 'False'} onChange={handleControlChange} />
      )}
    </Field>
  );

  const isLastStep = activeStep === stepTitles.length - 1;

  return (
    <Page className="add-teacher">
      <PageHeader
        eyebrow="Profiles"
        title="Add Teacher"
        subtitle="Create a teacher profile in four steps. Nothing is saved until you press Save on the last step."
        actions={
          <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <div className="add-teacher__form">
        <WizardSteps label="Add teacher steps" steps={stepTitles} active={activeStep} onSelect={setActiveStep} />

        {/* Step Forms */}
        <form className="add-teacher__form" noValidate>
          {activeStep === 0 && (
            <>
              <Card title="Personal details" subtitle={`Step 1 of ${stepTitles.length}`}>
                <div className="add-teacher__grid">
                  <Field label="First name" required>
                    {(ctl) => (
                      <div className="add-teacher__pair">
                        <Select
                          aria-label="Title"
                          name="title"
                          options={titleOptions}
                          value={formData.title || ''}
                          onChange={handleControlChange}
                        />
                        <TextInput
                          {...ctl}
                          type="text"
                          placeholder={EnterFirstName}
                          name="firstName"
                          value={formData.firstName || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    )}
                  </Field>
                  <Field label="Middle name" required>
                    {(ctl) => (
                      <TextInput
                        {...ctl}
                        type="text"
                        name="middleName"
                        placeholder={MiddleName}
                        value={formData.middleName || ''}
                        onChange={handleInputChange}
                      />
                    )}
                  </Field>
                  <Field label="Last name" required>
                    {(ctl) => (
                      <TextInput
                        {...ctl}
                        type="text"
                        name="lastName"
                        placeholder={EnterLastName}
                        value={formData.lastName || ''}
                        onChange={handleInputChange}
                      />
                    )}
                  </Field>
                  <Field label="Gender" required>
                    {(ctl) => (
                      <Select {...ctl} name="gender" options={genderOptions} value={formData.gender || ''} onChange={handleControlChange} />
                    )}
                  </Field>
                  <Field label="Date of birth" required>
                    {(ctl) => (
                      <DatePicker
                        id={ctl.id}
                        className="ds-input"
                        wrapperClassName="add-teacher__datepicker"
                        selected={birthDate}
                        onChange={handleBirthDateChange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText={BirthDateJob}
                      />
                    )}
                  </Field>
                </div>
              </Card>

              <Card title="Contact and location">
                <div className="add-teacher__grid">
                  <Field label="Email" required>
                    {(ctl) => (
                      <TextInput
                        {...ctl}
                        placeholder={EmailJobPlaceholder}
                        type="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleInputChange}
                      />
                    )}
                  </Field>
                  <Field label="Mobile number" required>
                    {(ctl) => (
                      <div className="add-teacher__pair">
                        <Select
                          aria-label="Country code"
                          name="countryCode"
                          options={countryCodeOptions}
                          value={formData.countryCode || ''}
                          onChange={handleControlChange}
                        />
                        <TextInput
                          {...ctl}
                          type="tel"
                          placeholder={DelimiterPlaceholder}
                          name="mobileNumber"
                          value={formData.mobileNumber || ""}
                          onChange={handleInputChange}
                          maxLength={String(formData.countryCode || "").trim() === "+91" ? 10 : 15}
                          inputMode="numeric"
                        />
                      </div>
                    )}
                  </Field>
                  <Field label="Time zone" required>
                    {(ctl) => (
                      <Select {...ctl} name="timeZone" options={timezoneOptions} value={formData.timeZone || ''} onChange={handleControlChange} />
                    )}
                  </Field>
                  <Field label="State" required hint={states.length === 0 ? "Loading states..." : undefined}>
                    {(ctl) => (
                      <Select {...ctl} name="state" options={stateOptions} value={formData.state || ''} onChange={handleControlChange} />
                    )}
                  </Field>
                </div>
              </Card>

              <Card title="Profile media">
                <div className="add-teacher__grid">
                  <Field label="Photo link" required>
                    {(ctl) => (
                      <TextInput {...ctl} type="text" name="photoLink" value={formData.photoLink || ''} onChange={handleInputChange} />
                    )}
                  </Field>
                  <Field label="Video link" required>
                    {(ctl) => (
                      <TextInput {...ctl} type="text" name="videoLink" value={formData.videoLink || ''} onChange={handleInputChange} />
                    )}
                  </Field>
                </div>
              </Card>
            </>
          )}

          {activeStep === 1 && (
            <Card title="Academic details" subtitle={`Step 2 of ${stepTitles.length}`}>
              <div className="add-teacher__grid">
                <Field label="Academic qualifications" required>
                  {(ctl) => (
                    <TextInput {...ctl} type="text" name="qualifications" value={formData.qualifications || ''} onChange={handleInputChange} />
                  )}
                </Field>
                <Field label="Years of experience" required>
                  {(ctl) => (
                    <TextInput {...ctl} type="number" name="experience" value={formData.experience || ''} onChange={handleInputChange} />
                  )}
                </Field>
                {boolField('isAcademicExpert', 'Enable for demo (AE)')}
                {boolField('enableForPaid', 'Enable for paid')}
              </div>
            </Card>
          )}

          {activeStep === 2 && (
            <Card title="Employment and eligibility" subtitle={`Step 3 of ${stepTitles.length}`}>
              <div className="add-teacher__grid">
                <Field label="Employment type" required>
                  {(ctl) => (
                    <Select
                      {...ctl}
                      name="employmentType"
                      options={employmentOptions}
                      value={formData.employmentType || ''}
                      onChange={handleControlChange}
                    />
                  )}
                </Field>
                {boolField('eligibleOffline', 'Eligible for offline batches')}
                {boolField('eligibleOnline', 'Eligible for online batches')}
                {boolField('eligibleOneToOne', 'Eligible for 1:1 classes')}
              </div>
            </Card>
          )}

          {activeStep === 3 && (
            <Card title="Other preferences and courses" subtitle={`Step 4 of ${stepTitles.length}`}>
              <div className="add-teacher__grid">
                {boolField('onlyInternational', 'Only international')}
                {boolField('onlyDomestic', 'Only domestic')}
                {boolField('markedForOffers', 'Marked for offers')}
                {boolField('kidsFriendly', 'Kids friendly', YES_NO)}
                <Field label="Preferred language" required>
                  {(ctl) => (
                    <ReactSelect<Option, true>
                      inputId={ctl.id}
                      isMulti
                      value={formData.languages || []}
                      options={languageOptions}
                      onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'languages')}
                      placeholder="Select language"
                      classNamePrefix={DS_SELECT_PREFIX}
                      styles={dsSelectStyles<Option, true>()}
                    />
                  )}
                </Field>
                <Field label="Age group" required>
                  {(ctl) => (
                    <ReactSelect<Option, true>
                      inputId={ctl.id}
                      isMulti
                      value={formData.ageGroup || []}
                      options={ageGroupOptions}
                      onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'ageGroup')}
                      placeholder="Select age group"
                      classNamePrefix={DS_SELECT_PREFIX}
                      styles={dsSelectStyles<Option, true>()}
                    />
                  )}
                </Field>
                <Field label="Category" required={Boolean(formData.isAcademicExpert)}>
                  {(ctl) => (
                    <Select
                      {...ctl}
                      name="aeCourseCategoryId"
                      options={categoryOptions}
                      value={formData.aeCourseCategoryId || ''}
                      onChange={handleControlChange}
                      disabled={!formData.isAcademicExpert}
                    />
                  )}
                </Field>
                <Field label="Description" required>
                  {(ctl) => (
                    <TextArea {...ctl} name="description" value={formData.description || ''} onChange={handleControlChange} />
                  )}
                </Field>
              </div>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="add-teacher__actions">
            <Button variant="secondary" onClick={prevStep} disabled={activeStep === 0}>Previous</Button>
            {!isLastStep && (
              <Button variant="primary" onClick={nextStep}>Next</Button>
            )}
            {isLastStep && (
              <Button variant="primary" onClick={handleSubmit}>Save</Button>
            )}
          </div>
        </form>
      </div>
    </Page>
  );
};

export default MultiStepForm;
